import React, { useState, useCallback } from "react";
import "../style/useranalytics.css";
import logo from "../../CareCoordinator/assets/logo (2).png";

import { useAnalytics }            from "../hooks/useAnalytics";
import { fetchAnalyticsDashboard }  from "../api/analyticsapi";
import { DATE_RANGES, TABS }       from "../constants/analyticsConstants";
import { TabNav }                  from "../components/shared/UIComponents";
import PatientSelector             from "../components/PatientSelector";
import OverviewTab                 from "../components/OverviewTab";
import EventsTab                   from "../components/EventsTab";
import ScreensTab                  from "../components/ScreensTab";
import JourneysTab                 from "../components/JourneyTab";
import LiveFeedTab                 from "../components/LiveFeedTab";

export default function UserAnalytics() {
  const [tab,             setTab]             = useState("Overview");
  const [range,           setRange]           = useState("30d");
  const [live,            setLive]            = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const patientId = selectedPatient?.owner_id ?? null;

  // Overview always shows ALL users — never filtered
  const overviewData = useAnalytics(range, null);

  // All other tabs respect the patient filter
  const filteredData = useAnalytics(range, patientId);

  // Pick the right data source per tab
  const activeHook = tab === "Overview" ? overviewData : filteredData;
  const { data, loading, error, refetch } = activeHook;

  const fetchLatest = useCallback(
    () => fetchAnalyticsDashboard(range, tab === "Overview" ? null : patientId),
    [range, patientId, tab]
  );

  const renderTab = () => {
    if (loading) return <LoadingState />;
    if (error)   return <ErrorState message={error} onRetry={refetch} />;
    if (!data)   return null;
    switch (tab) {
      case "Overview":  return <OverviewTab  data={data} />;
      case "Features":  return <EventsTab    data={data} />;
      case "Screens":   return <ScreensTab   data={data} />;
      case "Journeys":  return <JourneysTab  data={data} />;
      case "Live Feed": return <LiveFeedTab  data={data} live={live} onFetchLatest={fetchLatest} />;
      default:          return null;
    }
  };

  // PatientSelector is hidden on Overview (always shows all)
  const showPatientSelector = tab !== "Overview";

  return (
    <div
      className="ua-root"
      style={{
        position: "relative",
        width: "100vw",
        maxWidth: "100vw",
        marginLeft: "-50vw",
        marginRight: "-50vw",
        left: "50%",
        right: "50%",
      }}
    >
      {/* ── Sticky top bar ── */}
      <header className="ua-topbar">
        <div className="ua-brand">
          {logo
            ? <div className="ua-brand-logo"><img src={logo} alt="logo" /></div>
            : <div className="ua-brand-logo-fallback">📊</div>
          }
          <div className="ua-brand-text">
            <div className="ua-brand-title">User Analytics</div>
            <div className="ua-brand-sub">Events · sessions · journeys</div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Patient filter — only visible outside Overview */}
          {showPatientSelector && (
            <>
              <PatientSelector
                selectedPatient={selectedPatient}
                onSelect={setSelectedPatient}
              />
              <div style={{ width: 1, height: 24, background: "rgba(99,102,241,.15)" }} />
            </>
          )}

          {/* Range + controls */}
          <div className="ua-controls">
            {DATE_RANGES.map((r) => (
              <button
                key={r}
                className={`ua-range-btn${range === r ? " active" : ""}`}
                onClick={() => setRange(r)}
              >{r}</button>
            ))}
            <button
              className={`ua-live-btn${live ? " live" : ""}`}
              onClick={() => setLive(l => !l)}
            >
              <span className="ua-live-dot" />
              {live ? "Live" : "Paused"}
            </button>
            <button className="ua-refresh-btn" onClick={refetch} title="Refresh">↻</button>
          </div>
        </div>
      </header>

      {/* ── Sticky tab bar ── */}
      <nav className="ua-tabbar">
        <TabNav tabs={TABS} active={tab} onChange={setTab} />

        {/* Patient context pill — shown in tabbar when a patient is selected */}
        {showPatientSelector && selectedPatient && (
          <div style={{
            marginLeft: "auto",
            display: "flex", alignItems: "center", gap: 8,
            fontSize: 12, color: "#5B5A8A",
            background: "rgba(99,102,241,.07)",
            border: "1px solid rgba(99,102,241,.14)",
            borderRadius: 999, padding: "4px 14px",
            fontFamily: "'DM Sans',sans-serif",
            flexShrink: 0,
          }}>
            <span style={{ color: "#4F46E5", fontWeight: 700 }}>
              {selectedPatient.user_profile?.name}
            </span>
            <span style={{ opacity: .4 }}>·</span>
            <span style={{ color: "#9B99C4" }}>
              {[
                selectedPatient.user_profile?.age && `${selectedPatient.user_profile.age}y`,
                selectedPatient.user_profile?.gender,
              ].filter(Boolean).join(" · ")}
            </span>
            <button
              onClick={() => setSelectedPatient(null)}
              style={{
                border: "none", background: "transparent",
                cursor: "pointer", color: "#9B99C4",
                fontSize: 14, lineHeight: 1, padding: "0 0 0 4px",
              }}
            >×</button>
          </div>
        )}

        {/* "All patients" label shown in Overview */}
        {tab === "Overview" && (
          <div style={{
            marginLeft: "auto",
            fontSize: 11, color: "#9B99C4",
            fontFamily: "'DM Sans',sans-serif",
            display: "flex", alignItems: "center", gap: 5,
          }}>
            <span>👥</span> All patients · aggregate view
          </div>
        )}
      </nav>

      {/* ── Scrollable content ── */}
      <main className="ua-page">
        {renderTab()}
      </main>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="ua-vstack">
      <div className="ua-grid-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="ua-skeleton" style={{ height: 124 }} />
        ))}
      </div>
      <div className="ua-grid-2-1">
        <div className="ua-skeleton" style={{ height: 196 }} />
        <div className="ua-skeleton" style={{ height: 196 }} />
      </div>
      <div className="ua-grid-1-1">
        <div className="ua-skeleton" style={{ height: 220 }} />
        <div className="ua-skeleton" style={{ height: 220 }} />
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="ua-error-state">
      <div className="ua-error-icon">⚠️</div>
      <div className="ua-error-title">Failed to load analytics</div>
      <div className="ua-error-msg">{message}</div>
      <button className="ua-retry-btn" onClick={onRetry}>Try again</button>
    </div>
  );
}